// Packages and components
import React, { useState } from 'react';
import { Box } from 'grommet';
import SHDButton from '@ui/Button/SHDButton';
import SHDCheckbox from '@ui/Checkbox/SHDCheckbox';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInput from '@ui/Input/SHDInput';
import SHDInputDate from '@ui/InputDate/SHDInputDate';
import SHDInputTime from '@ui/InputTime/SHDInputTime';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDRule from '@ui/Rule/SHDRule';
import SHDSelect from '@ui/Select/SHDSelect';
import SHDSelectable from '@ui/Selectable/SHDSelectable';
import SHDText from '@ui/Text/SHDText';

// Helpers and utilities
import { calculateTs } from '@helpers/utils';
import { getFutureStringDate, getStringDate, getTime } from '@theme/utils/data';
import useGame from '@hooks/useGame';

// Types and enums
import type { SelectOption } from '@theme/theme.d';

const NextGamePage: React.FC<{
  onContinue: (trackingData: object) => void;
  teamId: string;
  teamName: string;
  sportType: string;
  modalHeight: { height: string; min: string };
}> = ({ onContinue, teamId, sportType, modalHeight }) => {
  const [view, setView] = useState<'exactDate' | 'estimate'>('exactDate');
  const [time, setTime] = useState<SelectOption>(getTime());
  const [date, setDate] = useState<string | string[]>(getStringDate());
  const [checked, setChecked] = useState<boolean>(false);
  const [selectableOption, setSelectableOption] = useState<SelectOption>({
    label: 'Next week',
    value: 7,
  });
  const [opponent, setOpponent] = useState<string>('');
  const { createGame, loading } = useGame();

  const handleCreateGame = async () => {
    const scheduleTsStart = calculateTs(date as string, time?.value as string);
    const trackingData: Record<string, string | string[] | undefined> = {
      gameDateType: view,
      selectedDate:
        view === 'exactDate' ? `${date} - ${time?.value}` : undefined,
      estimatedDate: view === 'estimate' ? date : undefined,
      opponentName: opponent || 'TBD Opponent',
    };
    try {
      const gameDetails = {
        date,
        time: time?.value,
        opponent: opponent ? opponent : 'TBD Opponent',
        scheduleTsStart,
        teamId,
        sportType,
      };
      await createGame(gameDetails);
      onContinue(trackingData);
    } catch (err) {
      throw new Error(err as string);
    }
  };

  return (
    <Box height={modalHeight}>
      <SHDModalContainer>
        <Box width="60%" margin={{ bottom: 'xxxlarge' }}>
          <SHDHeading level="3">When is your next game?</SHDHeading>
        </Box>
        <Box direction="row" gap="xxsmall" width="250px">
          <SHDSelectable
            id={'exactDate'}
            isActive={view === 'exactDate'}
            onSelected={() => {
              setView('exactDate'),
                setOpponent(''),
                setDate(getStringDate()),
                setTime(getTime());
            }}
            round="33px"
            pad={{ horizontal: 'small', vertical: 'xsmall' }}
          >
            <SHDText
              style="subhead"
              color={view === 'exactDate' ? 'green' : 'black'}
            >
              Exact Date
            </SHDText>
          </SHDSelectable>

          <SHDSelectable
            id={'estimate'}
            isActive={view === 'estimate'}
            onSelected={() => {
              setView('estimate'),
                setOpponent(''),
                setTime({ label: selectableOption.label, value: '00:00' }),
                setDate(getFutureStringDate(selectableOption.value as number));
            }}
            round="33px"
            pad={{ horizontal: 'small', vertical: 'xsmall' }}
          >
            <SHDText
              style="subhead"
              color={view === 'estimate' ? 'green' : 'black'}
            >
              Estimate
            </SHDText>
          </SHDSelectable>
        </Box>
      </SHDModalContainer>

      <SHDRule />

      {view === 'exactDate' && (
        <SHDModalContainer>
          <Box gap="12px">
            <Box direction="row" align="center" justify="between" gap="medium">
              <SHDText style="subhead-semibold">Date</SHDText>
              <Box width="170px">
                <SHDInputDate
                  onChange={(value) => setDate(value)}
                  label=""
                  showIcon={true}
                />
              </Box>
            </Box>

            <Box direction="row" align="center" justify="between" gap="medium">
              <SHDText style="subhead-semibold">Time</SHDText>
              <Box width="100px">
                <SHDInputTime
                  onChange={(value) => setTime(value)}
                  label=""
                  disabled={checked}
                  minuteStep={5}
                />
              </Box>
            </Box>

            <Box direction="row" align="center" gap="small">
              <SHDCheckbox
                onChange={(value) => setChecked(value)}
                checked={checked}
                label=""
              />
              <SHDText style="body-semibold">
                I don&apos;t know the exact time
              </SHDText>
            </Box>
          </Box>
        </SHDModalContainer>
      )}

      {view === 'estimate' && (
        <SHDModalContainer>
          <Box direction="row" align="center" justify="between" gap="medium">
            <SHDText style="subhead-semibold">Sometime...</SHDText>
            <Box width="200px">
              <SHDSelect
                label=""
                onChange={(selection) => {
                  setSelectableOption(selection);
                  setTime({ label: selection.label, value: '00:00' }); //for test now always set time to 00:00 we can change this
                  if (selection.value === 'placeholder') {
                    setDate('1970-01-01');
                  } else {
                    setDate(getFutureStringDate(selection.value as number));
                  }
                }}
                options={[
                  { label: 'Next week', value: 7 }, // 7 days from now
                  { label: 'In a few weeks', value: 21 }, // 21 days from now
                  { label: 'Next month', value: 30 }, // 30 days from now
                  { label: 'Next season', value: 90 }, // 90 days from now
                  { label: 'Not sure yet', value: 'placeholder' }, // Placeholder date
                ]}
                value={selectableOption}
              />
            </Box>
          </Box>
        </SHDModalContainer>
      )}

      <SHDRule />

      <SHDModalContainer>
        <SHDInput
          label="Opponent team name"
          onChange={(value) => setOpponent(value)}
          value={opponent}
          maxLength={20}
        />
      </SHDModalContainer>

      <SHDFooter>
        <SHDModalContainer>
          <SHDButton
            disabled={loading}
            primary
            label={loading ? 'Saving...' : 'Next'}
            onClick={handleCreateGame}
          />
        </SHDModalContainer>
      </SHDFooter>
    </Box>
  );
};

export default NextGamePage;
